<div #grid class="table-plan-canvas grid grid-cols-1 gap-4 lg:min-h-0 lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden" [ngClass]="gridColsClass">
  <!-- TVG-189: left editor pane — an accordion of Templates and Furniture.
       Opening one section collapses the other; selecting a template swaps to
       Furniture. Everything scrolls inside the pane so no control slides behind
       the canvas. Hidden in view-only mode. -->
  @if (interactive) {
    <aside class="catalogue rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-white/[0.03] lg:h-full lg:min-h-0 lg:overflow-y-auto">
      <!-- Templates section -->
      <div
        class="border-gray-200 dark:border-gray-800"
        [class.mb-3]="expandedSection === 'templates'"
        [class.border-b]="expandedSection === 'templates'"
        [class.pb-3]="expandedSection === 'templates'">
        <div class="flex items-center justify-between gap-1.5">
          <button
            type="button"
            class="flex flex-1 items-center gap-1.5 text-left"
            [attr.aria-expanded]="expandedSection === 'templates'"
            title="Show or hide the saved templates"
            (click)="toggleSection('templates')">
            <lucide-icon [name]="expandedSection === 'templates' ? ChevronDownIcon : ChevronRightIcon" class="h-3.5 w-3.5 text-gray-400"></lucide-icon>
            <lucide-icon [name]="LayoutIcon" class="h-3.5 w-3.5 text-gray-500"></lucide-icon>
            <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Templates</h4>
            @if (venueTemplates.length > 0) {
              <span class="text-[10px] font-normal text-gray-400">({{ venueTemplates.length }})</span>
            }
          </button>
          <button
            type="button"
            class="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-200 dark:hover:bg-brand-900/30"
            title="Save the current canvas as a new template for this venue"
            (click)="openSaveTemplate()">
            <lucide-icon [name]="SaveIcon" class="h-3.5 w-3.5"></lucide-icon>
            Save current
          </button>
        </div>

        @if (expandedSection === 'templates') {
          <!-- TVG-189: save the current canvas as a new named template. In-pane
               input (no browser prompt) so it works everywhere and is obvious. -->
          <div class="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900/40">
            <label class="mb-1 block text-[11px] font-semibold text-gray-700 dark:text-gray-200">
              Save current canvas as a template
            </label>
            <div class="flex items-center gap-1">
              <input
                #templateNameInput
                type="text"
                [(ngModel)]="newTemplateName"
                (keyup.enter)="submitNewTemplate()"
                placeholder="Template name"
                class="h-8 min-w-0 flex-1 rounded border border-gray-300 bg-white px-2 text-[11px] text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
              <button
                type="button"
                class="flex shrink-0 items-center gap-1 rounded bg-brand-600 px-2 py-1.5 text-[11px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40"
                title="Save the current canvas as a new template"
                [disabled]="!hasTables || !newTemplateName.trim()"
                (click)="submitNewTemplate()">
                <lucide-icon [name]="SaveIcon" class="h-3.5 w-3.5"></lucide-icon>
                Save
              </button>
            </div>
            @if (!hasTables) {
              <p class="mt-1 text-[10px] leading-tight text-gray-500 dark:text-gray-400">
                Place some tables on the canvas first, then save them as a template.
              </p>
            }
          </div>

          <p class="mb-2 mt-3 text-[11px] leading-tight text-gray-500 dark:text-gray-400">
            Preview a saved template on the room, then Select to lock it in for this event. Everything stays editable after.
          </p>
          @if (venueTemplates.length > 0) {
            <div class="space-y-2">
              @for (template of venueTemplates; track template.id) {
                <div
                  class="overflow-hidden rounded-lg border bg-white dark:bg-gray-900"
                  [class.border-brand-500]="isSelectedTemplate(template) || isPreviewingTemplate(template)"
                  [class.ring-2]="isSelectedTemplate(template)"
                  [class.ring-brand-500]="isSelectedTemplate(template)"
                  [class.border-gray-200]="!isSelectedTemplate(template) && !isPreviewingTemplate(template)"
                  [class.dark:border-gray-700]="!isSelectedTemplate(template) && !isPreviewingTemplate(template)">
                  <div class="relative aspect-[4/3] w-full bg-gray-50 dark:bg-gray-900/40">
                    <app-layout-preview
                      class="absolute inset-0 p-1"
                      [outline]="outline"
                      [tables]="tablesForTemplate(template)"
                      [ariaLabel]="template.name + ', seats ' + template.seat_total">
                    </app-layout-preview>
                    @if (isSelectedTemplate(template)) {
                      <span class="absolute right-1 top-1 rounded bg-brand-600 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                        Selected
                      </span>
                    } @else if (isPreviewingTemplate(template)) {
                      <span class="absolute right-1 top-1 rounded bg-amber-500/90 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                        Preview
                      </span>
                    }
                  </div>
                  <div class="px-2 py-1.5">
                    <!-- TVG-227: inline rename — click the pencil to edit the name in place. -->
                    @if (isRenaming(template)) {
                      <div class="flex items-center gap-1">
                        <input
                          #renameInput
                          type="text"
                          [(ngModel)]="renameDraft"
                          (keyup.enter)="submitRename(template)"
                          (keyup.escape)="cancelRename()"
                          placeholder="Template name"
                          class="h-6 min-w-0 flex-1 rounded border border-gray-300 bg-white px-1.5 text-[11px] text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
                        <button
                          type="button"
                          class="shrink-0 rounded p-1 text-emerald-600 hover:bg-emerald-50 disabled:opacity-40 dark:hover:bg-emerald-900/30"
                          title="Save name"
                          [disabled]="!renameDraft.trim()"
                          (click)="submitRename(template)">
                          <lucide-icon [name]="SelectIcon" class="h-3.5 w-3.5"></lucide-icon>
                        </button>
                        <button
                          type="button"
                          class="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                          title="Cancel rename"
                          (click)="cancelRename()">
                          <lucide-icon [name]="CloseIcon" class="h-3.5 w-3.5"></lucide-icon>
                        </button>
                      </div>
                    } @else {
                      <div class="truncate text-[11px] font-semibold text-gray-900 dark:text-white" [title]="template.name">
                        {{ template.name }}
                      </div>
                    }
                    <div class="text-[10px] text-gray-500 dark:text-gray-400">
                      {{ template.seat_total }} seats · {{ template.table_count }} tables
                      @if (templateSurplus(template) > 0) {
                        · {{ templateSurplus(template) }} to remove
                      }
                    </div>
                    <div class="mt-1.5 flex items-center gap-1">
                      <button
                        type="button"
                        [class]="isPreviewingTemplate(template)
                          ? 'flex flex-1 items-center justify-center gap-1 rounded border border-brand-500 bg-brand-50 px-1.5 py-1 text-[11px] font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-200'
                          : 'flex flex-1 items-center justify-center gap-1 rounded border border-gray-200 px-1.5 py-1 text-[11px] font-medium text-gray-600 dark:border-gray-700 dark:text-gray-300'"
                        [attr.aria-pressed]="isPreviewingTemplate(template)"
                        (click)="previewVenueTemplate(template)">
                        <lucide-icon [name]="PreviewIcon" class="h-3 w-3"></lucide-icon>
                        {{ isPreviewingTemplate(template) ? 'Hide' : 'Preview' }}
                      </button>
                      <button
                        type="button"
                        [class]="isSelectedTemplate(template)
                          ? 'flex flex-1 items-center justify-center gap-1 rounded bg-emerald-600 px-1.5 py-1 text-[11px] font-semibold text-white disabled:cursor-default'
                          : 'flex flex-1 items-center justify-center gap-1 rounded bg-brand-600 px-1.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-700'"
                        [disabled]="isSelectedTemplate(template)"
                        [title]="isSelectedTemplate(template) ? 'This template is selected for the event' : 'Load this template and lock it in for the event'"
                        (click)="selectVenueTemplate(template)">
                        <lucide-icon [name]="SelectIcon" class="h-3 w-3"></lucide-icon>
                        {{ isSelectedTemplate(template) ? 'Selected' : 'Select' }}
                      </button>
                      <button
                        type="button"
                        class="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                        title="Rename this template"
                        (click)="startRename(template)">
                        <lucide-icon [name]="RenameIcon" class="h-3.5 w-3.5"></lucide-icon>
                      </button>
                      <button
                        type="button"
                        class="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-300"
                        title="Delete this saved template"
                        (click)="emitDeleteTemplate(template)">
                        <lucide-icon [name]="TrashIcon" class="h-3.5 w-3.5"></lucide-icon>
                      </button>
                    </div>
                    <!-- TVG-227: once a template is selected for the event, edit the
                         room and save the changes straight back onto it. -->
                    @if (isSelectedTemplate(template)) {
                      <button
                        type="button"
                        class="mt-1.5 flex w-full items-center justify-center gap-1 rounded border border-brand-200 bg-brand-50 px-1.5 py-1 text-[11px] font-medium text-brand-700 hover:bg-brand-100 dark:border-brand-900/40 dark:bg-brand-900/20 dark:text-brand-200 dark:hover:bg-brand-900/40"
                        title="Overwrite this template with the current canvas layout"
                        (click)="emitUpdateLayout(template)">
                        <lucide-icon [name]="SaveIcon" class="h-3 w-3"></lucide-icon>
                        Save canvas to this template
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          } @else {
            <p class="text-[11px] leading-tight text-gray-500 dark:text-gray-400">
              No saved templates for this venue yet. Arrange the room, then Save.
            </p>
          }
        }
      </div>

      <!-- Furniture section -->
      <div>
        <button
          type="button"
          class="flex w-full items-center gap-1.5 text-left"
          [attr.aria-expanded]="expandedSection === 'furniture'"
          title="Show or hide the furniture tiles"
          (click)="toggleSection('furniture')">
          <lucide-icon [name]="expandedSection === 'furniture' ? ChevronDownIcon : ChevronRightIcon" class="h-3.5 w-3.5 text-gray-400"></lucide-icon>
          <lucide-icon [name]="FurnitureIcon" class="h-3.5 w-3.5 text-gray-500"></lucide-icon>
          <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Furniture</h4>
        </button>

        @if (expandedSection === 'furniture') {
          <p class="mb-3 mt-2 text-xs text-gray-500 dark:text-gray-400">Drag onto the room. Drop a trestle on the end of another to make a run.</p>
          <!-- TVG-373: true-size furniture. Thumbnails are small; pieces render
               at real size once placed in the room. -->
          <div class="grid grid-cols-1 gap-2">
            @for (type of TILE_TYPES; track trackByTileType($index, type)) {
              @let thumb = catalogueThumb(type);
              <div
                class="catalogue-tile flex cursor-grab select-none flex-col items-center rounded-lg border border-gray-200 bg-white p-1.5 hover:border-brand-400 active:cursor-grabbing dark:border-gray-700 dark:bg-gray-900"
                [title]="'Drag ' + TILE_LABEL[type] + ' onto the room'"
                (pointerdown)="startCatalogueDrag($event, type)">
                <svg class="block h-10 w-16" [attr.viewBox]="thumb.viewBox" preserveAspectRatio="xMidYMid meet">
                  <g [attr.transform]="thumb.piece.transform">
                    @for (rect of thumb.piece.trestles; track $index) {
                      <rect [attr.x]="rect.x" [attr.y]="rect.y" [attr.width]="rect.w" [attr.height]="rect.h"
                        [attr.fill]="PLAN_COLOURS.table" [attr.stroke]="PLAN_COLOURS.tableEdge" stroke-width="4" />
                    }
                    @for (chair of thumb.piece.chairs; track $index) {
                      <rect [attr.x]="chair.x" [attr.y]="chair.y" [attr.width]="chair.w" [attr.height]="chair.h"
                        rx="10" [attr.fill]="PLAN_COLOURS.emptyChair" [attr.stroke]="PLAN_COLOURS.emptyChairEdge" stroke-width="4" />
                    }
                  </g>
                </svg>
                <span class="mt-1 text-center text-[10px] leading-tight text-gray-600 dark:text-gray-300">{{ TILE_LABEL[type] }}</span>
              </div>
            }
            <!-- TVG-373: a single chair, dragged onto any table side. -->
            <div
              class="catalogue-tile flex cursor-grab select-none items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white p-1.5 hover:border-brand-400 active:cursor-grabbing dark:border-gray-700 dark:bg-gray-900"
              title="Drag a chair onto any side of a table"
              (pointerdown)="startChairAdd($event)">
              <span class="inline-block h-3 w-4 rounded-sm border-2 bg-white" [style.border-color]="PLAN_COLOURS.emptyChairEdge" aria-hidden="true"></span>
              <span class="text-[10px] leading-tight text-gray-600 dark:text-gray-300">Chair</span>
            </div>
          </div>

          <!-- TVG-379: walking space is kept clear round every table; this
               shades it on all of them so the clearances can be checked. -->
          <label class="mt-3 flex cursor-pointer items-start gap-2 text-xs text-gray-600 dark:text-gray-300" data-testid="walking-space-toggle">
            <input type="checkbox" class="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
              [checked]="showWalkingSpace" (change)="showWalkingSpace = !showWalkingSpace" />
            <span>Show walking space <span class="text-gray-400">({{ furniture.walkingSpace }} cm round each table)</span></span>
          </label>

          <!-- TVG-216: fixed-area markers — visual placeholders (bar, dance
               floor, toilets) dragged onto the room and resized freely. No
               seating or collision logic; tables can sit over or around them. -->
          <div class="mt-4 border-t border-gray-200 pt-3 dark:border-gray-800">
            <h4 class="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Fixed areas</h4>
            <p class="mb-2 text-[11px] leading-tight text-gray-500 dark:text-gray-400">
              Drag an area onto the room, then move or resize it. Areas are markers only — tables can sit over them.
            </p>
            <div class="grid grid-cols-1 gap-2">
              @for (m of MARKER_CATALOGUE; track trackByMarkerType($index, m)) {
                <div
                  class="marker-catalogue-tile flex cursor-grab select-none items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 hover:border-brand-400 active:cursor-grabbing dark:border-gray-700 dark:bg-gray-900"
                  [title]="'Drag ' + m.label + ' onto the room'"
                  (pointerdown)="startMarkerCatalogueDrag($event, m)">
                  <span
                    class="inline-block h-5 w-8 shrink-0 rounded-sm border-2"
                    [style.background-color]="MARKER_STYLE[m.type].fill"
                    [style.border-color]="MARKER_STYLE[m.type].stroke"
                    aria-hidden="true"></span>
                  <span class="text-[11px] leading-tight text-gray-600 dark:text-gray-300">{{ m.label }}</span>
                </div>
              }
            </div>
          </div>

          @if (unplacedTables.length > 0) {
            <div class="mt-4 border-t border-gray-200 pt-3 dark:border-gray-800">
              <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Unplaced ({{ unplacedTables.length }})
              </h4>
              <ul class="mt-1 space-y-1 text-xs text-gray-700 dark:text-gray-300">
                @for (t of unplacedTables; track trackById($index, t)) {
                  <li>{{ t.label }} — {{ t.seat_count }} seats</li>
                }
              </ul>
            </div>
          }
        }
      </div>
    </aside>
  }

  <!-- Canvas -->
  <div class="canvas-wrap relative flex flex-col rounded-2xl border border-gray-200 bg-white p-2 dark:border-gray-800 dark:bg-white/[0.03] lg:h-full lg:min-h-0">
    <!-- Status + control strip -->
    <div class="status-strip mb-2 flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs dark:border-gray-800 dark:bg-gray-900/60">
      @if (interactive) {
        <!-- TVG-142: tables carry their full chairs, so this is a plain
             "add a chair" source rather than a guest-count-capped pool. -->
        <div
          class="chair-pool-pill flex cursor-grab select-none items-center gap-1.5 active:cursor-grabbing"
          (pointerdown)="startChairAdd($event)"
          title="Drag a chair onto a free position to add it">
          <span class="inline-block h-4 w-7 rounded border border-gray-400 bg-gray-300 dark:bg-gray-600" aria-hidden="true"></span>
          <span class="text-gray-600 dark:text-gray-300">Add chair</span>
        </div>
        <span class="h-3 w-px bg-gray-300 dark:bg-gray-700" aria-hidden="true"></span>
      }

      <div class="flex items-center gap-1.5" [title]="placementMismatch ? 'Placed guests do not match the guest total — advisory only, the plan still saves' : 'All guests placed'">
        <span class="text-gray-600 dark:text-gray-300">Placed</span>
        <span
          class="font-bold"
          [class.text-amber-600]="placementMismatch"
          [class.dark:text-amber-400]="placementMismatch"
          [class.text-gray-900]="!placementMismatch"
          [class.dark:text-white]="!placementMismatch">
          {{ placedGuestCount }} / {{ totalGuestCount }}
        </span>
      </div>

      <span class="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-500 dark:text-gray-400">
        @if (interactive) {
          <button
            type="button"
            class="flex items-center gap-1 rounded px-1.5 py-1 font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-40 dark:text-brand-200 dark:hover:bg-brand-900/30"
            [disabled]="!hasTables"
            title="Save the current canvas as a new template for this venue"
            (click)="openSaveTemplate()">
            <lucide-icon [name]="SaveIcon" class="h-3.5 w-3.5"></lucide-icon>
            Save as template
          </button>
          <button
            type="button"
            class="flex items-center gap-1 rounded px-1.5 py-1 font-medium text-red-600 hover:bg-red-50 disabled:opacity-40 dark:text-red-300 dark:hover:bg-red-900/30"
            [disabled]="saving || !hasTables"
            title="Clear all tables from the plan"
            (click)="clearAll()">
            <lucide-icon [name]="EraserIcon" class="h-3.5 w-3.5"></lucide-icon>
            Clear all
          </button>
        }
      </span>
    </div>

    <!-- TVG-189: preview action bar — while a template is previewed on the
         canvas, Select it (persist + lock in) or cancel back to the current
         plan. -->
    @if (interactive && isPreviewing && previewTemplate; as pt) {
      <div class="mb-2 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-brand-300 bg-brand-50 px-3 py-1.5 text-xs dark:border-brand-800 dark:bg-brand-900/20">
        <span class="font-medium text-brand-800 dark:text-brand-100">
          Previewing <span class="font-semibold">{{ pt.name }}</span> · {{ pt.seat_total }} seats · {{ guestCount }} guests
        </span>
        <span class="ml-auto flex items-center gap-2">
          <button
            type="button"
            class="rounded px-2 py-1 font-medium text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800"
            (click)="cancelPreview()">
            Cancel
          </button>
          <button
            type="button"
            class="rounded bg-brand-600 px-2.5 py-1 font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            [disabled]="saving"
            (click)="applyPreview()">
            {{ hasTables ? 'Select — replace current tables' : 'Select this template' }}
          </button>
        </span>
      </div>
    }

    <!-- TVG-373: the plan key. -->
    <div class="plan-key mb-1.5 flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 px-1 text-[11px] text-gray-600 dark:text-gray-300" aria-label="Plan key">
      @for (item of PLAN_KEY; track item.kind) {
        <span class="flex items-center gap-1.5">
          <svg class="h-3.5 w-5" viewBox="0 0 20 14" aria-hidden="true">
            @switch (item.kind) {
              @case ('empty') {
                <rect x="1.5" y="1.5" width="17" height="11" rx="3" [attr.fill]="PLAN_COLOURS.emptyChair" [attr.stroke]="PLAN_COLOURS.emptyChairEdge" stroke-width="1.5" />
              }
              @case ('seated') {
                <rect x="1.5" y="1.5" width="17" height="11" rx="3" [attr.fill]="PLAN_COLOURS.seatedChair" />
              }
              @case ('dietary') {
                <circle cx="10" cy="7" r="5" [attr.fill]="PLAN_COLOURS.dietary" />
              }
              @case ('allergy') {
                <circle cx="10" cy="7" r="5" [attr.fill]="PLAN_COLOURS.allergy" />
              }
              @case ('high') {
                <rect x="1.5" y="1.5" width="17" height="11" rx="3" [attr.fill]="PLAN_COLOURS.emptyChair" [attr.stroke]="PLAN_COLOURS.highChair" stroke-width="2.5" />
              }
            }
          </svg>
          {{ item.label }}
        </span>
      }
    </div>

    <!-- TVG-145: the room sits in a fixed-height stage so a true-to-scale room
         (the Great Barn is ~6.7 m × 17.1 m, taller than the viewport) fits
         inside the visible box instead of expanding its container and turning
         the window into the scroller. At lg the SVG is taken out of flow and
         meet-scaled to fill the stage, so the canvas and status strip stay put
         and only the guest list scrolls. Below lg it flows at natural height. -->
    <div class="canvas-stage relative w-full lg:flex-1 lg:min-h-0">
    <svg
      #svg
      class="block h-auto w-full select-none lg:absolute lg:inset-0 lg:h-full lg:w-full"
      [attr.viewBox]="viewBox"
      preserveAspectRatio="xMidYMid meet"
      (click)="onCanvasBackgroundClick()">
      <defs>
        <pattern id="grid" [attr.width]="GRID_STEP" [attr.height]="GRID_STEP" patternUnits="userSpaceOnUse">
          <path [attr.d]="'M ' + GRID_STEP + ' 0 L 0 0 0 ' + GRID_STEP" fill="none" [attr.stroke]="PLAN_COLOURS.grid" stroke-width="2" />
        </pattern>
        <clipPath id="roomClip"><path [attr.d]="outline.fillPath" /></clipPath>
      </defs>

      <!-- Room fill + grid (grid clipped to the room interior) -->
      <path [attr.d]="outline.fillPath" fill="#ffffff" />
      <rect
        [attr.x]="-MARGIN" [attr.y]="-MARGIN"
        [attr.width]="outline.width + 2 * MARGIN" [attr.height]="outline.height + 2 * MARGIN"
        fill="url(#grid)" clip-path="url(#roomClip)" />

      <!-- Walls. TVG-373: an opening (doorway, gap) is drawn as a true gap in
           the wall, with a short jamb at each side, never as a line. -->
      @for (wall of outline.walls; track $index) {
        @if (wall.opening) {
          @for (jamb of jambsFor(wall); track $index) {
            <line [attr.x1]="jamb.x1" [attr.y1]="jamb.y1" [attr.x2]="jamb.x2" [attr.y2]="jamb.y2"
              [attr.stroke]="PLAN_COLOURS.wall" stroke-width="5" stroke-linecap="round" />
          }
        } @else {
          <line
            [attr.x1]="wall.x1" [attr.y1]="wall.y1" [attr.x2]="wall.x2" [attr.y2]="wall.y2"
            [attr.stroke]="PLAN_COLOURS.wall" stroke-width="7" stroke-linecap="round" />
        }
        <text
          [attr.x]="wall.labelX" [attr.y]="wall.labelY"
          [attr.transform]="'rotate(' + wall.angle + ' ' + wall.labelX + ' ' + wall.labelY + ')'"
          text-anchor="middle" dominant-baseline="central"
          font-size="32" fill="#6b7280" font-weight="600">
          {{ wall.label }}
        </text>
      }

      <!-- TVG-373: dotted lines inside the room (the Milling Barn DJ alcove's
           sliding doors). Labels only; they never block a table. -->
      @for (divider of outline.dividers; track $index) {
        <line [attr.x1]="divider.x1" [attr.y1]="divider.y1" [attr.x2]="divider.x2" [attr.y2]="divider.y2"
          [attr.stroke]="PLAN_COLOURS.wall" stroke-width="4" stroke-dasharray="4 12" stroke-linecap="round" />
        <text [attr.x]="(divider.x1 + divider.x2) / 2" [attr.y]="(divider.y1 + divider.y2) / 2 + 30"
          text-anchor="middle" dominant-baseline="central" font-size="24" fill="#4b5563" font-style="italic">
          {{ divider.label }}
        </text>
      }

      <!-- TVG-372: fixed features (kitchen, bar, toilets, doors, outside areas)
           are labels only, drawn under the tables so they never block one. -->
      @for (feature of outline.features; track feature.label) {
        <text
          [attr.x]="feature.x" [attr.y]="feature.y"
          [attr.text-anchor]="feature.anchor" dominant-baseline="central"
          font-size="36" fill="#4b5563" font-weight="600">
          {{ feature.label }}
        </text>
      }

      <!-- TVG-216: fixed-area markers (bar, dance floor, toilets). Rendered
           under the tables so tables can be placed over or around them. Drag the
           box to move it; when selected, drag the corner handle to resize. -->
      @for (marker of markers; track trackById($index, marker)) {
        @let mb = markerBox(marker);
        <g
          class="marker"
          [attr.opacity]="isPreviewing ? 0.2 : 1"
          [class.marker-selected]="marker.id === selectedMarkerId"
          (click)="selectMarker($event, marker)"
          (pointerdown)="startMarkerDrag($event, marker)">
          <rect
            [attr.x]="mb.x" [attr.y]="mb.y" [attr.width]="mb.w" [attr.height]="mb.h"
            rx="6"
            [attr.fill]="MARKER_STYLE[marker.type].fill"
            [attr.stroke]="MARKER_STYLE[marker.type].stroke"
            stroke-width="3" stroke-dasharray="10 6"
            [class.marker-grab]="interactive" />
          <text
            [attr.x]="mb.x + mb.w / 2" [attr.y]="mb.y + mb.h / 2"
            text-anchor="middle" dominant-baseline="central" font-size="26"
            [attr.fill]="MARKER_STYLE[marker.type].text" font-weight="700" pointer-events="none">
            {{ marker.label }}
          </text>
          @if (interactive && marker.id === selectedMarkerId) {
            <rect
              class="marker-resize-handle"
              [attr.x]="mb.x + mb.w - 20" [attr.y]="mb.y + mb.h - 20"
              width="26" height="26" rx="4"
              fill="#ffffff" [attr.stroke]="MARKER_STYLE[marker.type].stroke" stroke-width="3"
              (pointerdown)="startMarkerResize($event, marker)">
              <title>Drag to resize</title>
            </rect>
          }
        </g>
      }

      <!-- TVG-373: walking space round the table being dragged or selected
           (TVG-379: or round every table while Show walking space is on). -->
      @for (table of placedTables; track trackById($index, table)) {
        @if (interactive && (showWalkingSpace || isDragging(table) || table.id === selectedTableId)) {
          @let space = spaceBoxFor(table);
          <rect class="tile-space" pointer-events="none"
            [attr.x]="space.x1" [attr.y]="space.y1" [attr.width]="space.x2 - space.x1" [attr.height]="space.y2 - space.y1"
            rx="8" [attr.stroke]="isFlagged(table) ? PLAN_COLOURS.tooClose : PLAN_COLOURS.selected" />
        }
      }

      <!-- Placed tables (TVG-373: true size, chairs spread along each side) -->
      @for (table of placedTables; track trackById($index, table)) {
        <g
          [attr.transform]="tileTransform(table)"
          [attr.opacity]="isPreviewing ? 0.2 : 1"
          [class.tile-selected]="table.id === selectedTableId"
          (click)="selectTable($event, table)"
          (pointerdown)="startTileDrag($event, table)">
          @for (rect of trestlesFor(table); track $index) {
            <rect
              [attr.x]="rect.x" [attr.y]="rect.y" [attr.width]="rect.w" [attr.height]="rect.h"
              [attr.fill]="PLAN_COLOURS.table" [attr.stroke]="PLAN_COLOURS.tableEdge" stroke-width="3" class="tile-trestle" />
          }

          @let size = tableSizeFor(table);
          <!-- Selected: dashed outline. Blocked or too close: red outline. -->
          @if (isFlagged(table)) {
            <rect class="tile-flag" pointer-events="none" x="-6" y="-6" [attr.width]="size.w + 12" [attr.height]="size.h + 12"
              rx="6" fill="none" [attr.stroke]="PLAN_COLOURS.tooClose" stroke-width="6" />
          } @else if (table.id === selectedTableId) {
            <rect pointer-events="none" x="-6" y="-6" [attr.width]="size.w + 12" [attr.height]="size.h + 12"
              rx="6" fill="none" [attr.stroke]="PLAN_COLOURS.selected" stroke-width="4" stroke-dasharray="14 8" />
          }

          <text
            [attr.x]="size.w / 2" [attr.y]="size.h / 2"
            [attr.transform]="'rotate(' + (-table.rotation) + ' ' + size.w / 2 + ' ' + size.h / 2 + ')'"
            text-anchor="middle" dominant-baseline="central" font-size="22" [attr.fill]="PLAN_COLOURS.seatedChair" font-weight="600" pointer-events="none">
            {{ table.label }}{{ table.is_top_table ? ' ★' : '' }}
          </text>

          @for (chair of chairsFor(table); track trackById($index, chair.seat)) {
            @let seat = chair.seat;
            <g
              [attr.data-seat-id]="seat.id"
              class="tile-chair"
              [class.chair-hovered]="hoveredSeatId === seat.id"
              [class.chair-drop-target]="guestDrag?.overSeatId === seat.id"
              [class.chair-dragging]="chairDrag?.moved && chairDrag?.seat?.id === seat.id"
              (pointerenter)="hoveredSeatId = seat.id"
              (pointerleave)="hoveredSeatId === seat.id ? hoveredSeatId = null : null"
              (click)="$event.stopPropagation()">
              <rect
                [attr.x]="chair.x" [attr.y]="chair.y" [attr.width]="chair.w" [attr.height]="chair.h"
                rx="10" ry="10"
                [attr.fill]="seatFill(seat)" [attr.stroke]="seatStroke(seat)" [attr.stroke-width]="chair.high ? 5 : 3"
                [class.chair-grab]="interactive"
                (pointerdown)="startChairDrag($event, seat, table)" />
              <title>{{ seatTitle(seat) }}</title>

              @if (seat.guest) {
                <text
                  [attr.x]="chair.cx" [attr.y]="chair.cy"
                  [attr.transform]="'rotate(' + (-table.rotation) + ' ' + chair.cx + ' ' + chair.cy + ')'"
                  text-anchor="middle" dominant-baseline="central" font-size="17" font-weight="700"
                  fill="#ffffff" pointer-events="none">
                  {{ initialsFor(seat.guest) }}
                </text>
              }

              <!-- Marks: dietary, allergy and high chair, along the chair's edge. -->
              @let markY = chair.orientation === 'horizontal' ? chair.y + 2 : chair.y + 6;
              @let markX = chair.orientation === 'horizontal' ? chair.x + 6 : chair.x + 2;
              @if (seatHasDietary(seat)) {
                <circle [attr.cx]="markX" [attr.cy]="markY" r="7" [attr.fill]="PLAN_COLOURS.dietary" stroke="#ffffff" stroke-width="2" pointer-events="none" />
              }
              @if (seatHasAllergy(seat)) {
                <circle [attr.cx]="chair.orientation === 'horizontal' ? markX + 14 : markX" [attr.cy]="chair.orientation === 'horizontal' ? markY : markY + 14"
                  r="7" [attr.fill]="PLAN_COLOURS.allergy" stroke="#ffffff" stroke-width="2" pointer-events="none" />
              }
              @if (chair.high) {
                <circle [attr.cx]="chair.x + chair.w - 4" [attr.cy]="chair.y + chair.h - 4" r="7" [attr.fill]="PLAN_COLOURS.highChair" stroke="#ffffff" stroke-width="2" pointer-events="none" />
              }

              <!-- Hover menu: high chair, remove chair, clear guest. -->
              @if (interactive && hoveredSeatId === seat.id && !chairDrag && !dragState) {
                @let menu = chairMenuPosition(chair, size, seat.guest ? 3 : 2);
                <g class="chair-hover-controls" (pointerdown)="$event.stopPropagation()" [attr.transform]="'translate(' + menu.x + ' ' + menu.y + ') rotate(' + (-table.rotation) + ')'">
                  <g class="chair-control" transform="translate(0 0)" (click)="toggleHighChair(seat, $event)">
                    <circle r="24" fill="white" [attr.stroke]="PLAN_COLOURS.highChair" stroke-width="3" />
                    <text x="0" y="9" text-anchor="middle" font-size="26" font-weight="700" [attr.fill]="PLAN_COLOURS.highChair" pointer-events="none">H</text>
                    <title>{{ seat.is_high_chair ? 'Make a normal chair' : 'Make a high chair' }}</title>
                  </g>
                  <g class="chair-control" transform="translate(56 0)" (click)="removeChair(seat, $event)">
                    <circle r="24" fill="white" stroke="#6b7280" stroke-width="3" />
                    <text x="0" y="10" text-anchor="middle" font-size="32" font-weight="700" fill="#374151" pointer-events="none">−</text>
                    <title>Remove this chair</title>
                  </g>
                  @if (seat.guest) {
                    <g class="chair-control" transform="translate(112 0)" (click)="unassignSeat(seat, $event)">
                      <circle r="24" fill="white" stroke="#b91c1c" stroke-width="3" />
                      <text x="0" y="10" text-anchor="middle" font-size="32" font-weight="700" fill="#b91c1c" pointer-events="none">×</text>
                      <title>Clear this seat</title>
                    </g>
                  }
                </g>
              }
            </g>
          }

          <!-- TVG-373: chair drop strips on every side that can take a chair. -->
          @if (showSideTargets) {
            @for (strip of sideTargetsFor(table); track strip.side) {
              <rect
                [attr.data-side-slot]="table.id + ':' + strip.side"
                [attr.x]="strip.x" [attr.y]="strip.y" [attr.width]="strip.w" [attr.height]="strip.h"
                rx="6" class="chair-free-slot"
                [class.chair-free-slot-active]="isSideTarget(table.id, strip.side)" />
            }
          }
        </g>
      }

      <!-- TVG-197: ghost preview of the layout being considered. Non-interactive;
           the generated tables always sit at rotation 0, so a plain translate is
           enough (matches the left-pane thumbnail). -->
      @if (isPreviewing) {
        <g class="layout-ghost" pointer-events="none">
          <!-- TVG-216: ghost the saved template's markers alongside its tables. -->
          @for (m of previewMarkers; track $index) {
            <g>
              <rect
                [attr.x]="m.position_x" [attr.y]="m.position_y" [attr.width]="m.width" [attr.height]="m.height"
                rx="6"
                [attr.fill]="MARKER_STYLE[m.type].fill"
                [attr.stroke]="MARKER_STYLE[m.type].stroke"
                stroke-width="4" stroke-dasharray="12 8" />
              <text
                [attr.x]="m.position_x + m.width / 2" [attr.y]="m.position_y + m.height / 2"
                text-anchor="middle" dominant-baseline="central" font-size="26"
                [attr.fill]="MARKER_STYLE[m.type].text" font-weight="700">
                {{ m.label }}
              </text>
            </g>
          }
          @for (t of previewTables; track $index) {
            @let piece = previewPieceFor(t);
            <g [attr.transform]="piece.transform">
              @for (rect of piece.trestles; track $index) {
                <rect
                  [attr.x]="rect.x" [attr.y]="rect.y" [attr.width]="rect.w" [attr.height]="rect.h"
                  [attr.fill]="PLAN_COLOURS.table" [attr.stroke]="PLAN_COLOURS.tableEdge"
                  stroke-width="4" stroke-dasharray="12 8" />
              }
              @for (chair of piece.chairs; track $index) {
                <rect
                  [attr.x]="chair.x" [attr.y]="chair.y" [attr.width]="chair.w" [attr.height]="chair.h"
                  rx="8" [attr.fill]="PLAN_COLOURS.emptyChair" [attr.stroke]="PLAN_COLOURS.emptyChairEdge" stroke-width="3" />
              }
            </g>
          }
        </g>
      }
    </svg>
    </div>

    <!-- Floating selection toolbar -->
    @if (interactive && !dragState && selectedTable; as t) {
      @let pos = toolbarPosition(t);
      @if (pos) {
        <div
          class="fixed z-30 flex flex-col gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
          [style.left.px]="pos.left"
          [style.top.px]="pos.top">
          <button
            type="button"
            class="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-800"
            [disabled]="saving"
            (click)="rotateSelected()">
            <lucide-icon [name]="RotateIcon" class="h-3.5 w-3.5"></lucide-icon>
            Turn
          </button>
          <!-- TVG-373: lengthen or shorten a run by a trestle. -->
          @if (canResize(t)) {
            <button
              type="button"
              class="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-800"
              [disabled]="saving"
              title="Add a trestle to the end of this table"
              (click)="lengthenSelected()">
              <lucide-icon [name]="PlusIcon" class="h-3.5 w-3.5"></lucide-icon>
              Trestle
            </button>
            <button
              type="button"
              class="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-800"
              [disabled]="saving || trestlesOf(t) <= 1"
              title="Take a trestle off the end of this table"
              (click)="shortenSelected()">
              <lucide-icon [name]="MinusIcon" class="h-3.5 w-3.5"></lucide-icon>
              Trestle
            </button>
          }
          <span class="px-2 text-[10px] text-gray-400">{{ trestlesOf(t) }} trestle{{ trestlesOf(t) === 1 ? '' : 's' }} · {{ t.seats.length }} seats</span>
          <button
            type="button"
            class="flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-amber-50 disabled:opacity-50 dark:hover:bg-amber-900/30"
            [class.text-amber-700]="t.is_top_table"
            [class.dark:text-amber-300]="t.is_top_table"
            [class.text-gray-700]="!t.is_top_table"
            [class.dark:text-gray-200]="!t.is_top_table"
            [disabled]="saving"
            (click)="toggleTopTable()">
            <lucide-icon [name]="CrownIcon" class="h-3.5 w-3.5"></lucide-icon>
            {{ t.is_top_table ? 'Not top table' : 'Top table' }}
          </button>
          <button
            type="button"
            class="flex items-center gap-1 rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-900/30"
            [disabled]="saving"
            (click)="deleteSelected()">
            <lucide-icon [name]="TrashIcon" class="h-3.5 w-3.5"></lucide-icon>
            Delete
          </button>
          <button
            type="button"
            class="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            (click)="closeSelection()">
            <lucide-icon [name]="CloseIcon" class="h-3.5 w-3.5"></lucide-icon>
            Close
          </button>
        </div>
      }
    }

    <!-- TVG-216: floating toolbar for a selected fixed-area marker. -->
    @if (interactive && selectedMarker; as m) {
      @let mpos = markerToolbarPosition(m);
      @if (mpos) {
        <div
          class="fixed z-30 flex flex-col gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
          [style.left.px]="mpos.left"
          [style.top.px]="mpos.top">
          <span class="px-2 pt-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{{ m.label }}</span>
          <button
            type="button"
            class="flex items-center gap-1 rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-900/30"
            [disabled]="saving"
            (click)="deleteSelectedMarker()">
            <lucide-icon [name]="TrashIcon" class="h-3.5 w-3.5"></lucide-icon>
            Delete
          </button>
          <button
            type="button"
            class="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            (click)="closeMarkerSelection()">
            <lucide-icon [name]="CloseIcon" class="h-3.5 w-3.5"></lucide-icon>
            Close
          </button>
        </div>
      }
    }
  </div>

  <!-- Guest sidebar -->
  <aside class="guest-sidebar flex flex-col rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-white/[0.03] lg:h-full lg:min-h-0">
    <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
      Guests ({{ guests.length }})
    </h4>
    @if (interactive) {
      <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">
        Drag a guest onto a chair to assign, or click to open their record.
      </p>
    }

    <!-- TVG-142: type-to-search to quickly find a guest in a long list. -->
    <input
      type="search"
      [value]="guestSearch"
      (input)="setGuestSearch($any($event.target).value)"
      placeholder="Search guests…"
      class="mb-2 h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />

    <div class="mb-2 inline-flex rounded-lg border border-gray-200 p-0.5 text-xs dark:border-gray-700">
      <button
        type="button"
        class="rounded-md px-2 py-1 font-medium"
        [class.bg-brand-500]="guestFilter === 'all'"
        [class.text-white]="guestFilter === 'all'"
        [class.text-gray-600]="guestFilter !== 'all'"
        [class.dark:text-gray-300]="guestFilter !== 'all'"
        (click)="setGuestFilter('all')">
        All {{ guests.length }}
      </button>
      <button
        type="button"
        class="rounded-md px-2 py-1 font-medium"
        [class.bg-brand-500]="guestFilter === 'unseated'"
        [class.text-white]="guestFilter === 'unseated'"
        [class.text-gray-600]="guestFilter !== 'unseated'"
        [class.dark:text-gray-300]="guestFilter !== 'unseated'"
        (click)="setGuestFilter('unseated')">
        Not seated {{ unseatedCount }}
      </button>
      <button
        type="button"
        class="rounded-md px-2 py-1 font-medium"
        [class.bg-brand-500]="guestFilter === 'seated'"
        [class.text-white]="guestFilter === 'seated'"
        [class.text-gray-600]="guestFilter !== 'seated'"
        [class.dark:text-gray-300]="guestFilter !== 'seated'"
        (click)="setGuestFilter('seated')">
        Seated {{ seatedCount }}
      </button>
    </div>

    @if (guests.length === 0) {
      <p class="text-sm text-gray-500 dark:text-gray-400">No guests yet on this event.</p>
    } @else if (filteredGuests.length === 0) {
      <p class="text-sm text-gray-500 dark:text-gray-400">No guests match.</p>
    }
    <ul class="max-h-[calc(100vh-9rem)] flex-1 space-y-1 overflow-y-auto lg:max-h-none lg:min-h-0">
      @for (guest of filteredGuests; track trackByGuest($index, guest)) {
        @let placement = guestPlacement(guest.id);
        <li
          class="guest-pill flex items-center gap-2 rounded-md border border-gray-200 px-2 py-1 text-sm dark:border-gray-700"
          [class.cursor-grab]="interactive"
          [class.active:cursor-grabbing]="interactive"
          [attr.title]="interactive ? 'Drag onto a chair to assign · click to open record' : null"
          (pointerdown)="interactive && startGuestDrag($event, guest)">
          <span
            class="relative h-6 w-6 shrink-0 rounded-full text-center text-xs font-bold leading-6 text-white"
            [style.background-color]="PLAN_COLOURS.seatedChair">
            {{ initialsFor(guest) }}
            @if (guestHasDietary(guest)) {
              <span
                class="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border border-white"
                [style.background-color]="PLAN_COLOURS.dietary"
                title="Dietary requirement"></span>
            }
            @if (guest.has_allergy) {
              <span
                class="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full border border-white"
                [style.background-color]="PLAN_COLOURS.allergy"
                title="Allergy"></span>
            }
            @if (guest.requires_high_chair) {
              <span
                class="absolute -bottom-1 -right-1 h-2.5 w-2.5 rounded-full border border-white"
                [style.background-color]="PLAN_COLOURS.highChair"
                title="Needs a high chair"></span>
            }
          </span>
          <span class="flex-1 truncate text-gray-700 dark:text-gray-200">{{ guest.full_name }}</span>
          @if (placement) {
            <span class="shrink-0 text-right text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
              <span class="font-semibold text-gray-700 dark:text-gray-200">T{{ placement.table }}</span>
              · seat {{ placement.seat }}
            </span>
          }
        </li>
      }
    </ul>
  </aside>
</div>

<!-- Floating drag preview (catalogue drag) -->
@if (catalogueDrag) {
  <div
    class="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-1/2 rounded border-2 border-dashed border-brand-500 bg-brand-100/80 px-2 py-1 text-xs text-brand-700 shadow-lg dark:bg-brand-900/40 dark:text-brand-200"
    [style.left.px]="catalogueDrag.pointer.x"
    [style.top.px]="catalogueDrag.pointer.y">
    Placing {{ TILE_LABEL[catalogueDrag.tileType] }}
  </div>
}

<!-- Floating drag preview (marker catalogue drag — TVG-216) -->
@if (markerCatalogueDrag) {
  <div
    class="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-1/2 rounded border-2 border-dashed border-brand-500 bg-brand-100/80 px-2 py-1 text-xs text-brand-700 shadow-lg dark:bg-brand-900/40 dark:text-brand-200"
    [style.left.px]="markerCatalogueDrag.pointer.x"
    [style.top.px]="markerCatalogueDrag.pointer.y">
    Placing {{ markerCatalogueDrag.label }}
  </div>
}

<!-- Floating drag preview (guest drag) -->
@if (guestDrag) {
  <div
    class="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-brand-500 bg-white px-3 py-1 text-xs font-semibold text-brand-700 shadow-lg dark:bg-gray-900 dark:text-brand-200"
    [style.left.px]="guestDrag.pointer.x"
    [style.top.px]="guestDrag.pointer.y">
    {{ guestDrag.guest.full_name }}
  </div>
}

<!-- Floating drag preview (chair drag — TVG-132) -->
@if (chairDrag?.moved) {
  <div
    class="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-1/2 rounded-md border-2 border-gray-500 bg-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 shadow-lg dark:bg-gray-700 dark:text-gray-100"
    [style.left.px]="chairDrag!.pointer.x"
    [style.top.px]="chairDrag!.pointer.y">
    {{ chairDrag!.mode === 'add' ? 'Add chair — drop on a table side' : 'Chair — drop off the table to remove' }}
  </div>
}
